import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import { PanelLeft, Globe, LayoutDashboard, Database, Wrench, LogOut, Loader2, RefreshCw, Sun, Moon } from 'lucide-react';

import api from './api';
import { useScraperStatus } from './hooks/useScraperStatus';
import { useLeadSelection } from './hooks/useLeadSelection';
import { useAgent2 } from './hooks/useAgent2';
import { usePricing } from './hooks/usePricing';
import { useStripe } from './hooks/useStripe';

import AdminDashboardOverview from './components/AdminDashboardOverview';
import FlowConfig from './components/FlowConfig';
import ExtractionHistory from './components/ExtractionHistory';
import AdminLeadsTable from './components/AdminLeadsTable';
import ClientLeadsView from './components/ClientLeadsView';
import Agent2History from './components/Agent2History';
import Modal from './components/Modal';
import ValidationReportModal from './components/ValidationReportModal';

export default function App() {
  const navigate = useNavigate();
  const { user, userRole: firebaseRole, loading: authLoading, logout: firebaseLogout } = useAuth();
  const loggedIn = !!user;
  const userRole = firebaseRole || 'client';
  const isAdmin = userRole === 'admin';

  // Scraper hook
  const {
    isRunning, isPaused, status, results, setResults, resultsLoading,
    syncResults, startScraping, stopScraping, pauseScraping, resumeScraping, clearResults, resetLogs,
  } = useScraperStatus();

  // Stripe hook
  const { createCheckout, isProcessing: stripeProcessing } = useStripe();

  // Agent2 hook
  const { agent2History, agent2Running, agent2HistoryError, fetchAgent2History, validateBase } = useAgent2();

  // Filter state
  const [resultSearchInput, setResultSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [resultStateFilter, setResultStateFilter] = useState('all');
  const [contactFilter, setContactFilter] = useState('all');
  const [activeDashboardSection, setActiveDashboardSection] = useState('dashboard-overview');
  const [baseValidationReport, setBaseValidationReport] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem('leadszonemap_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('leadszonemap_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Search config state
  const [categories, setCategories] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchMode, setSearchMode] = useState('fila');
  const [dripMode, setDripMode] = useState(false);
  const [totalGoal, setTotalGoal] = useState(100);
  const [hours, setHours] = useState(12);
  const [waitMinutes, setWaitMinutes] = useState('');
  const [regionState, setRegionState] = useState('');
  const [country, setCountry] = useState('Brasil');
  const [selectedStates, setSelectedStates] = useState([]);
  const [statePicker, setStatePicker] = useState('');
  const [searchSequence, setSearchSequence] = useState([]);
  const [searchSequenceInput, setSearchSequenceInput] = useState('');
  const [extractSocials, setExtractSocials] = useState(false);
  const [headlessMode, setHeadlessMode] = useState(false);
  const [seqCity, setSeqCity] = useState('');
  const [seqNeighborhood, setSeqNeighborhood] = useState('');
  const [seqCoords, setSeqCoords] = useState('');
  const [seqRadius, setSeqRadius] = useState('');
  const [panelDrip, setPanelDrip] = useState(false);
  const [panelRegion, setPanelRegion] = useState(false);
  const [panelQueue, setPanelQueue] = useState(false);
  const [apiKey, setApiKey] = useState(localStorage.getItem('leadszonemap_api_key') || '');
  const [enterpriseType, setEnterpriseType] = useState(localStorage.getItem('leadszonemap_enterprise_type') || 'all');

  // Pricing
  const [pricingTier, setPricingTier] = useState('standard');
  const [pricingPacks, setPricingPacks] = useState(1);
  const { downloadPricingCSV } = usePricing(pricingTier, pricingPacks);

  // Derived filter data
  const activeResults = results.filter(r => r.Estado !== 'Excluído');
  const excludedResults = results.filter(r => r.Estado === 'Excluído');

  const normalizedSearch = resultSearchInput.trim().toLowerCase();
  const availableCategories = Array.from(new Set(activeResults.map((r) => r.Categoria).filter(Boolean))).sort();
  const availableSources = Array.from(new Set(activeResults.map((r) => r.Fonte).filter(Boolean))).sort();
  const availableResultStates = Array.from(new Set(activeResults.map((r) => r.Estado).filter(Boolean))).sort();

  const filteredResults = activeResults.filter((item) => {
    if (categoryFilter && categoryFilter.length > 0 && !categoryFilter.includes(item.Categoria)) return false;
    if (sourceFilter !== 'all' && item.Fonte !== sourceFilter) return false;
    if (resultStateFilter !== 'all' && item.Estado !== resultStateFilter) return false;
    if (contactFilter !== 'all') {
      if (contactFilter === 'whatsapp') {
        const hasPhone = Boolean(
          (item.WhatsApp && item.WhatsApp !== 'N/A') || 
          (item.Telefone && item.Telefone !== 'N/A')
        );
        if (!hasPhone) return false;
      } else if (contactFilter === 'email') {
        const hasEmail = Boolean(
          (Array.isArray(item.Emails) && item.Emails.length > 0) || 
          (typeof item.Email === 'string' && item.Email.includes('@'))
        );
        if (!hasEmail) return false;
      } else {
        const channels = Array.isArray(item.Canais) ? item.Canais : [];
        if (!channels.includes(contactFilter)) return false;
      }
    }
    if (!normalizedSearch) return true;
    const searchable = [
      item.Nome, item.Categoria, item.Endereco, item.Busca,
      item.SequenciaBusca, item.Estado, item.Fonte,
      ...(Array.isArray(item.PalavrasChave) ? item.PalavrasChave : []),
      ...(Array.isArray(item.Tags) ? item.Tags : []),
    ].join(' ').toLowerCase();
    return searchable.includes(normalizedSearch);
  });

  // Lead selection (client mode)
  const leadSelection = useLeadSelection(filteredResults);

  // Settings sync from SettingsPage
  useEffect(() => {
    const sync = () => {
      setApiKey(localStorage.getItem('leadszonemap_api_key') || '');
      setEnterpriseType(localStorage.getItem('leadszonemap_enterprise_type') || 'all');
    };
    window.addEventListener('leadszonemap-settings-updated', sync);
    sync();
    return () => window.removeEventListener('leadszonemap-settings-updated', sync);
  }, []);

  // Results polling
  const handleManualSync = () => syncResults(isAdmin, true);

  useEffect(() => {
    if (!loggedIn) return;
    handleManualSync();
    const interval = setInterval(handleManualSync, 4000);
    return () => clearInterval(interval);
  }, [loggedIn, isAdmin, syncResults]);

  useEffect(() => {
    if (loggedIn && isAdmin) {
      fetchAgent2History();
    }
  }, [loggedIn, isAdmin, fetchAgent2History]);

  // Hydrate state from active engine payload
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    if (isRunning && status?.payload && !hasHydrated) {
      const p = status.payload;
      setSearchMode(p.search_sequence ? 'avancado' : 'fila');
      if (p.categories) setCategories(p.categories);
      setTotalGoal(p.total_goal || 100);
      setHours(p.hours || 12);
      if (p.wait_minutes) setWaitMinutes(p.wait_minutes);
      if (p.drip_mode !== undefined) setDripMode(p.drip_mode);
      if (p.state) setRegionState(p.state);
      if (p.states) setSelectedStates(p.states);
      if (p.country) setCountry(p.country);
      if (p.search_sequence) setSearchSequence(p.search_sequence);
      setHasHydrated(true);
    }
  }, [isRunning, status, hasHydrated]);

  const handleLogout = useCallback(async () => {
    await firebaseLogout();
    navigate('/');
  }, [firebaseLogout, navigate]);

  // Screen switching (no scroll — each section is a full screen)

  // Search handlers
  const handleStart = async () => {
    resetLogs();
    
    if (!apiKey) {
      alert("⚠️ ERRO: Falta a Chave de API! Por favor, vá na aba 'Configurações' (⚙️) no painel e insira sua Google Maps API Key antes de iniciar a extração.");
      return;
    }

    // Convert advanced search objects to strings "key:value" or "value"
    let mappedSequence = null;
    if (searchMode === 'avancado' && searchSequence.length) {
      mappedSequence = searchSequence.map(s => {
        if (typeof s === 'object' && s !== null) {
          return s.type ? `${s.type}:${s.val}` : s.val;
        }
        return String(s);
      });
    }

    try {
      await startScraping({
        categories: searchMode === 'fila' ? categories : [],
        api_key: apiKey || null,
        limit: 20,
        drip_mode: dripMode,
        headless: headlessMode,
        total_goal: parseInt(totalGoal, 10) || 100,
        hours: parseInt(hours, 10) || 12,
        state: regionState || null,
        states: selectedStates.length ? selectedStates : null,
        wait_minutes: waitMinutes ? parseInt(waitMinutes, 10) || null : null,
        enterprise_type: enterpriseType,
        country: country.trim() || 'Brasil',
        search_sequence: mappedSequence,
        search_options: { extract_socials: extractSocials }
      });
    } catch (err) {
      const msg = err.response?.data?.detail 
        ? JSON.stringify(err.response.data.detail)
        : err.message;
      alert(`Erro ao iniciar extração: ${msg}. Verifique se todos os campos estão preenchidos corretamente (ex: API Key válida, números válidos).`);
    }
  };

  // Enviar alterações de opções ao backend caso esteja pausado
  useEffect(() => {
    if (isRunning && isPaused) {
      api.post('/update_options', {
        search_options: { extract_socials: extractSocials, headless: headlessMode }
      }).catch(console.error);
    }
  }, [extractSocials, headlessMode, isRunning, isPaused]);

  const handleDownload = async () => {
    const res = await api.get('/exports/workbook.xlsx', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `leadszonemap_exports_por_dia_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setTimeout(fetchAgent2History, 3500);
  };

  const handleValidateBase = async () => {
    const report = await validateBase();
    setBaseValidationReport(report);
  };

  const handleClearResults = () => {
    setConfirmModal({
      message: 'Deseja realmente limpar todos os resultados da tela?',
      onConfirm: async () => {
        await clearResults();
        toast.success('Resultados limpos com sucesso!');
      }
    });
  };

  // Auth guard
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem', background: 'var(--bg-main)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div className="client-topbar__logo-dot logo-pulse" style={{ width: 28, height: 28 }} />
          <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.03em' }}>LeadsZone<span style={{color: 'var(--accent)'}}>Map</span></h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <Loader2 size={36} className="spin" style={{ color: 'var(--accent)' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 500, letterSpacing: '0.02em' }}>Autenticando acesso...</p>
        </div>
      </div>
    );
  }
  if (!loggedIn) return <Navigate to="/login" replace />;

  // Client view
  if (!isAdmin) {
    if (resultsLoading) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem', background: 'var(--bg-main)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="client-topbar__logo-dot logo-pulse" style={{ width: 28, height: 28 }} />
            <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.03em' }}>LeadsZone<span style={{color: 'var(--accent)'}}>Map</span></h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%', maxWidth: '800px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 500, letterSpacing: '0.02em', alignSelf: 'center' }}>Carregando sua base de leads exclusiva&hellip;</p>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '1rem' }}>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px' }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.8 }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.6 }}></div>
              <div className="skeleton-box" style={{ height: '40px', width: '100%', borderRadius: '8px', opacity: 0.4 }}></div>
            </div>
          </div>
          <button onClick={handleLogout} style={{ marginTop: '1rem', background: 'transparent', border: '1px solid var(--glass-panel-border)', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', fontSize: '0.9rem', color: 'var(--text-secondary)', borderRadius: '30px', cursor: 'pointer', transition: 'all 0.2s' }}>
            <LogOut size={16} /> Sair
          </button>
        </div>
      );
    }

    return (
      <ClientLeadsView
        status={status}
        results={activeResults}
        filteredResults={filteredResults}
        {...leadSelection}
        pricingTier={pricingTier} setPricingTier={setPricingTier}
        resultSearchInput={resultSearchInput} setResultSearchInput={setResultSearchInput}
        categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
        sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
        resultStateFilter={resultStateFilter} setResultStateFilter={setResultStateFilter}
        contactFilter={contactFilter} setContactFilter={setContactFilter}
        availableCategories={availableCategories}
        availableSources={availableSources}
        availableResultStates={availableResultStates}
        onAddToWallet={() => {
          try { leadSelection.addSelectionToLeadWallet(); }
          catch (e) { toast.error(e.message); }
        }}
        onClearSelection={leadSelection.clearLeadSelection}
        onCheckout={async () => {
          try {
            const orderId = await leadSelection.confirmLeadWalletCheckout(pricingTier);
            toast.success(`Pedido criado! ID: ${orderId}. Redirecionando para pagamento...`);
            
            // Calc total price
            const packs = Math.max(1, Math.ceil(leadSelection.leadCartCount / 25)); // LEADS_PER_PACKAGE = 25
            const totalCents = (pricingTier === 'basic' ? 4500 : pricingTier === 'standard' ? 9000 : 18000) * packs;


            await createCheckout({
              exportId: orderId,
              quantity: packs,
              priceCents: totalCents,
              email: user.email,
              successUrl: `${window.location.origin}/dashboard?payment=success`,
              cancelUrl: `${window.location.origin}/dashboard?payment=cancelled`
            });
          } catch (e) { toast.error(e.message); }
        }}
        onLogout={handleLogout}
        onLeadCheckboxClick={leadSelection.handleLeadCheckboxClick}
        onDownloadPricingCSV={downloadPricingCSV}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    );
  }

  // Admin view
  const renderAdminScreen = () => {
    switch (activeDashboardSection) {
      case 'config':
        return (
          <div className="admin-screen animate-fade-in">
            <FlowConfig
              isRunning={isRunning} isPaused={isPaused}
              searchMode={searchMode} setSearchMode={setSearchMode}
              apiKey={apiKey} setApiKey={setApiKey}
              categories={categories} inputValue={inputValue} setInputValue={setInputValue}
              dripMode={dripMode} setDripMode={setDripMode}
              totalGoal={totalGoal} setTotalGoal={setTotalGoal}
              hours={hours} setHours={setHours}
              waitMinutes={waitMinutes} setWaitMinutes={setWaitMinutes}
              state={regionState} setState={setRegionState}
              country={country} setCountry={setCountry}
              selectedStates={selectedStates} statePicker={statePicker} setStatePicker={setStatePicker}
              searchSequence={searchSequence} searchSequenceInput={searchSequenceInput} setSearchSequenceInput={setSearchSequenceInput}
              seqCity={seqCity} setSeqCity={setSeqCity}
              seqNeighborhood={seqNeighborhood} setSeqNeighborhood={setSeqNeighborhood}
              seqCoords={seqCoords} setSeqCoords={setSeqCoords}
              seqRadius={seqRadius} setSeqRadius={setSeqRadius}
              enterpriseType={enterpriseType} setEnterpriseType={setEnterpriseType}
              extractSocials={extractSocials} setExtractSocials={setExtractSocials}
              headlessMode={headlessMode} setHeadlessMode={setHeadlessMode}

              panelDrip={panelDrip} setPanelDrip={setPanelDrip}
              panelRegion={panelRegion} setPanelRegion={setPanelRegion}
              panelQueue={panelQueue} setPanelQueue={setPanelQueue}
              status={status} results={activeResults} agent2Running={agent2Running}
              onStart={handleStart}
              onStop={stopScraping}
              onPause={pauseScraping}
              onResume={resumeScraping}
              onDownload={handleDownload}
              onValidateBase={handleValidateBase}
              onAddCategory={(e) => { if (e.key === 'Enter' && inputValue.trim()) { setCategories((p) => [...p, inputValue.trim()]); setInputValue(''); }}}
              onRemoveCategory={(i) => setCategories((p) => p.filter((_, idx) => idx !== i))}
              onAddSequence={(e) => { if (e.key === 'Enter' && searchSequenceInput.trim()) { setSearchSequence((p) => [...p, searchSequenceInput.trim()]); setSearchSequenceInput(''); }}}
              onRemoveSequence={(i) => setSearchSequence((p) => p.filter((_, idx) => idx !== i))}
              onAddSequenceItem={(key, value) => { if (!value?.trim()) return; setSearchSequence((p) => [...p, key ? `${key}:${value.trim()}` : value.trim()]); }}
              onAddState={() => { if (!statePicker || selectedStates.includes(statePicker)) return; setSelectedStates((p) => [...p, statePicker]); setStatePicker(''); }}
              onRemoveState={(i) => setSelectedStates((p) => p.filter((_, idx) => idx !== i))}
              onMoveState={(i, dir) => { const arr = [...selectedStates]; const ni = i + dir; if (ni < 0 || ni >= arr.length) return; const [item] = arr.splice(i, 1); arr.splice(ni, 0, item); setSelectedStates(arr); }}
              onSetStates={(states) => setSelectedStates(states)}
              onClearDidacticFields={() => { setSeqCity(''); setSeqNeighborhood(''); setSeqCoords(''); setSeqRadius(''); }}
            />

            <ExtractionHistory />

            <Agent2History
              agent2History={agent2History}
              agent2HistoryError={agent2HistoryError}
              onRefresh={fetchAgent2History}
            />
          </div>
        );

      case 'base':
        return (
          <div className="admin-screen animate-fade-in">
            <AdminLeadsTable
              status={status}
              results={activeResults}
              filteredResults={filteredResults}
              leadSelection={leadSelection} resultsLoading={resultsLoading}
              resultSearchInput={resultSearchInput} setResultSearchInput={setResultSearchInput}
              categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
              sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
              resultStateFilter={resultStateFilter} setResultStateFilter={setResultStateFilter}
              contactFilter={contactFilter} setContactFilter={setContactFilter}
              availableCategories={availableCategories}
              availableSources={availableSources}
              availableResultStates={availableResultStates}
              onClearResults={handleClearResults}
            />
          </div>
        );

      default:
        return (
          <div className="admin-screen animate-fade-in">
            <AdminDashboardOverview results={activeResults} excludedResults={excludedResults} status={status} />
          </div>
        );
    }
  };

  return (
    <div className="admin-portal">
      {/* ── Admin Top Bar ─────── */}
      <header className="admin-topbar">
        <div className="client-topbar__brand">
          <div className="client-topbar__logo-dot" />
          <span>LeadsZone Maps</span>
        </div>
        <div className="admin-topbar__nav">
          <button
            className={`client-topbar__tab ${activeDashboardSection === 'dashboard-overview' ? 'active' : ''}`}
            onClick={() => setActiveDashboardSection('dashboard-overview')}
          >
            <LayoutDashboard size={14} /> Dashboard
          </button>
          <button
            className={`client-topbar__tab ${activeDashboardSection === 'config' ? 'active' : ''}`}
            onClick={() => setActiveDashboardSection('config')}
          >
            <Wrench size={14} /> Configuração
          </button>
          <button
            className={`client-topbar__tab ${activeDashboardSection === 'base' ? 'active' : ''}`}
            onClick={() => setActiveDashboardSection('base')}
          >
            <Database size={14} /> Base
          </button>
          <button className="client-topbar__tab" onClick={handleManualSync}>
            <RefreshCw size={14} /> Sincronizar
          </button>
          <button className="client-topbar__tab" onClick={toggleTheme} title="Alternar tema">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />} Tema
          </button>
          <a
            href="https://www.whatszone.app.br"
            target="_blank"
            rel="noreferrer"
            className="client-topbar__tab"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
          >
            🚀 WhatsZone
          </a>
          <button className="client-topbar__logout" onClick={handleLogout}>
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      {/* ── Screen Content ──── */}
      <main className="admin-main">
        {renderAdminScreen()}
      </main>

      <Modal open={!!baseValidationReport} onClose={() => setBaseValidationReport(null)}>
        <h3 style={{ color: 'var(--accent)', marginBottom: '1rem' }}>Relatório de Validação da Base</h3>
        <ValidationReportModal report={baseValidationReport} />
      </Modal>

      <Modal open={!!confirmModal} onClose={() => setConfirmModal(null)}>
        {confirmModal && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#e74c3c' }}>Confirmação</h3>
            <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={() => setConfirmModal(null)}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#fff', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#e74c3c', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Sim, limpar base
              </button>
            </div>
          </div>
        )}
      </Modal>

      <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid var(--glass-panel-border)', fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 'auto', background: 'var(--bg-main)' }}>
        🚀 Quer disparar mensagens em massa no WhatsApp para esses leads? <a href="https://www.whatszone.app.br" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Conheça a ferramenta WhatsZone</a>
      </div>
    </div>
  );
}

